import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty, inRange, startCase } from "lodash";
import { useForm } from "react-hook-form";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../context/AuthContext";
import { apiGet, apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import { toast } from "react-toastify";
import Layout from "./Layout";
import { formatWithCommas } from "../Utils/Toast";
import iconBkash from "../assets/images/bkash.png";
import iconRocket from "../assets/images/rocket.png";
import iconNagad from "../assets/images/nagad.png";
import iconEth from "../assets/images/eth.svg";
import iconBtc from "../assets/images/btc.svg";
import iconUpay from "../assets/images/upay.png";
import bankImg from "../assets/images/bank.png";
import { validationRules } from "../Utils/constants";
import { FaRegCopy } from "react-icons/fa";

const AMOUNT_PRESETS = [200, 500, 1000, 2000, 10000, 20000];

// picks a known e-wallet icon by matching bank_name; falls back to a generic bank icon.
// if your API already returns an icon/logo url per bank, that's tried first (see getIcon below).
const matchIconByName = (bankName = "") => {
  const name = bankName.toLowerCase();
  if (name.includes("bkash")) return iconBkash;
  if (name.includes("nagad")) return iconNagad;
  if (name.includes("rocket")) return iconRocket;
  if (name.includes("eth")) return iconEth;
  if (name.includes("btc")) return iconBtc;
  if (name.includes("upay")) return iconUpay;
  return bankImg;
};

const getIcon = (bank) =>
  bank?.icon || bank?.logo || bank?.image || matchIconByName(bank?.bank_name);

const Deposit = () => {
  const { t } = useTranslation();
  const { type } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(!isEmpty(type) ? type : "deposit");

  // ---- dynamic payment methods (e-wallets, e.g. bKash/Nagad/Rocket) ----
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [methodsLoading, setMethodsLoading] = useState(true);

  // ---- dynamic deposit channels (gateway/processor, e.g. PayBangla/Bajipay) ----
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelsLoading, setChannelsLoading] = useState(true);

  const [dataOffer, setDataOffer] = useState([]);
  const [offer, setOffer] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(7);

  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);
  const userCoins = useContextSelector(AuthContext, (ctx) => ctx.userCoins);
  const amounutRefresh = useContextSelector(AuthContext, (ctx) => ctx.amounutRefresh);
  const refreshAmountLoader = useContextSelector(AuthContext, (ctx) => ctx.refreshAmountLoader);

  const [limit, setLimit] = useState(
    profileData?.limitSetting?.length > 0
      ? profileData?.limitSetting[0]
      : { minDeposit: "200", maxDeposit: "30000" },
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true });

  const watchedAmount = watch("amount");

  // ---- fetch bank/gateway list from the live API, same endpoint the old (commented-out) code used ----
  // NOTE: adjust the `type` values below ("wallet" / "deposit") to whatever your backend
  // actually expects if payment methods and deposit channels are distinguished differently.
  const getBankList = async (typeParam) => {
    const { status, data } = await apiPost(apiPath.getBank, { type: typeParam });
    if (status == 200 && data?.success) {
      return data?.results?.bankList || [];
    }
    return [];
  };

  const loadPaymentMethods = async () => {
    setMethodsLoading(true);

    try {
      const list = await getBankList("deposit");

      setPaymentMethods(list);

      if (list.length > 0) {
        setSelectedMethod(list[0]);
        console.log(list[0], "list[0]");
        setValue("accountName", list[0]?.account_name || "");
        setValue("bankAccount", list[0]?.account_number || "");
      }
    } catch (error) {
      console.error("Failed to load payment methods:", error);
      setPaymentMethods([]);
    } finally {
      setMethodsLoading(false);
    }
  };

  // const loadChannels = async () => {
  //   setChannelsLoading(true);
  //   const list = await getBankList("deposit");
  //   setChannels(list);
  //   if (list.length > 0) setSelectedChannel(list[0]);
  //   setChannelsLoading(false);
  // };

  useEffect(() => {
    loadPaymentMethods();
    // loadChannels();
  }, []);

  // ---- filter out already-claimed "first/second/third/fourth deposit" offers ----
  const getFilteredOfferList = (count, list) => {
    const depositKeysToRemove = [
      "OnFirstDeposit",
      "OnSecondDeposit",
      "OnThirdDeposit",
      "OneFourthDeposit",
    ];
    const keysToRemove = depositKeysToRemove.slice(0, count);
    return list.filter((o) => !keysToRemove.includes(o.offerOn));
  };

  const getData = async () => {
    const { status, data } = await apiGet(apiPath.getPromotional, {
      category: "Deposit",
    });
    if (status == 200 && data?.success) {
      let result = data?.results?.offerList?.filter((res) => res?.category == "Deposit");
      let updated = getFilteredOfferList(profileData?.userDepositCount, result);

      if (updated?.length > 0) {
        setDataOffer(updated);
        const defaultOffer = updated.find((res) => res?.offerOn == "OnDeposit");
        if (!isEmpty(defaultOffer)) {
          setOffer(defaultOffer?._id);
          // setLimit({
          //   minDeposit: defaultOffer?.minimumDeposite,
          //   maxDeposit: defaultOffer?.maximumDeposite,
          // });
        } else {
          setOffer(updated[0]?._id);
          // setLimit({
          //   minDeposit: updated[0]?.minimumDeposite,
          //   maxDeposit: updated[0]?.maximumDeposite,
          // });
        }
      }
    }
  };

  useEffect(() => {
    getData();
  }, [profileData]);

  useEffect(() => {
    if (!isEmpty(type)) setActiveTab(type);
  }, [type]);

  const onBonusChange = (e) => {
    const selectedId = e.target.value;
    const object = dataOffer?.find((res) => res?._id == selectedId);
    setOffer(selectedId);
    // setLimit({
    //   minDeposit: object?.minimumDeposite ?? "200",
    //   maxDeposit: object?.maximumDeposite ?? "30000",
    // });
  };
  const uploadImage = async (obj) => {
    let form = new FormData();
    form.append("TransactionFile", obj);
    const { status, data } = await apiPost(apiPath.reciptUpload, form);
    if (status == 200) {
      return data?.path;
    } else {
      return false;
    }
  };
  const [image, setImage] = useState("");
  const [id, setId] = useState("");
  const getBankDetail = async (body) => {
    setId(body);
    const { status, data } = await apiPost(apiPath.getBankDetail, {
      bank: body,
    });
    if (status == 200) {
      if (data?.success) {
        setValue("accountName", data?.results?.bankDetail?.account_name);
        setValue("bankAccount", data?.results?.bankDetail?.account_number);
      }
    }
  };
  const onSubmit = async (body) => {
    // if (!selectedMethod || !selectedChannel) {
    //   toast.error(t("SelectPaymentMethodChannel"));
    //   return;
    // }
    let imageCheck = "";

    if (image) {
      imageCheck = await uploadImage(image);
    }
    setLoading(true);

    const payload = {
      amount: body?.amount,

      bank: id,
      AccountName: body?.accountName,
      BankAccount: body?.bankAccount,
      transactionType: (selectedMethod?.bank_name || "").toUpperCase(),
      TransactionId: body?.transactionId,
      TransactionFile: image ? imageCheck : "",
      payment: "online",
      offer:offer

    };



    try {
      const { status, data } = await apiPost(apiPath.depositAmount, payload);
      if (status === 200) {
        if (data?.results) {
          toast.success(data?.message);

          setImage("");
          reset();
        } else {
          toast.error(data?.message);
        }
      } else {
        toast.error(data?.message);
        setLoading(false);

      }
    } catch (error) {
      toast.error(t("SomethingWentWrong"));
      setLoading(false);

    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(watch("bankAccount"))
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };
  const maskBankAccount = (value = "") => {
    if (!value) return "";
    const first4 = value.slice(0, 4);
    const masked = "*".repeat(Math.max(0, value.length - 4));
    return first4 + masked;
  };
  return (
    <Layout>
      <div>
        <div className="funds-tab">
          <div className="v-tabs v-tabs--centered v-tabs--fixed-tabs v-tabs--icons-and-text theme--dark">
            <div
              role="tablist"
              className="v-item-group theme--dark v-slide-group v-tabs-bar v-tabs-bar--is-mobile white--text"
              data-booted="true"
              style={{ height: "auto" }}
            >
              <div className="v-slide-group__prev v-slide-group__prev--disabled" />
              <div className="v-slide-group__wrapper">
                <div className="v-slide-group__content v-tabs-bar__content">
                  <a
                    tabIndex={0}
                    href="#deposit"
                    aria-selected={activeTab === "deposit"}
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("deposit");
                    }}
                    className={"v-tab" + (activeTab === "deposit" ? " v-tab--active" : "")}
                  >
                    {t("Deposit")}
                  </a>
                  <a
                    tabIndex={0}
                    href="#withdrawal"
                    aria-selected={activeTab === "withdraw"}
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/withdraw");
                    }}
                    className={"v-tab" + (activeTab === "withdraw" ? " v-tab--active" : "")}
                  >
                    {t("Withdrawal")}
                  </a>
                </div>
              </div>
              <div className="v-slide-group__next v-slide-group__next--disabled" />
            </div>
          </div>
        </div>

        <div className="v-window v-item-group theme--light v-tabs-items">
          <div className="v-window__container" style={{}}>
            <div className="v-window-item v-window-item--active" id="deposit">
              <div className="funds-container">
                <div
                  role="dialog"
                  className="v-dialog__container"
                  style={{ position: "relative", background: "transparent !important" }}
                />

                <div className="funds-wrapper v-card v-sheet theme--light elevation-0">
                  <div className="row deposit-wallet no-gutters">
                    <div onClick={() => amounutRefresh()} className="wallet-label col col-12">
                      <label className="white--text">{t("MainWallet")}</label>
                      <div className="refresh-balance-icon" />
                    </div>
                    <div className="balance col">
                      {refreshAmountLoader ? (
                        <div
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          className="v-progress-circular v-progress-circular--visible v-progress-circular--indeterminate amber--text"
                          style={{ height: "24px", width: "24px" }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="22.857142857142858 22.857142857142858 45.714285714285715 45.714285714285715"
                            style={{
                              "-webkit-transform": "rotate(0deg)",
                              "-ms-transform": "rotate(0deg)",
                              transform: "rotate(0deg)",
                            }}
                          >
                            <circle
                              fill="transparent"
                              cx="45.714285714285715"
                              cy="45.714285714285715"
                              r={20}
                              strokeWidth="5.714285714285714"
                              strokeDasharray="125.664"
                              strokeDashoffset="125.66370614359172px"
                              className="v-progress-circular__overlay"
                            />
                          </svg>
                          <div className="v-progress-circular__info" />
                        </div>
                      ) : (
                        <div className="text-capitalize">
                          <span className="white--text">
                            ৳ {formatWithCommas(userCoins?.totalCoins || 0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <form
                    noValidate="novalidate"
                    className="v-form"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {/* ---- Payment Methods (dynamic, from apiPath.getBank) ---- */}
                    <div className="section-container no-gutters col">
                      <div className="funds-title-wrapper">
                        <label className="text-capitalize subtitle-1 funds-title">
                          {t("PaymentMethods")}
                        </label>
                      </div>
                      <div className="funds-divider" />
                      <div className="row button-container no-gutters">
                        {methodsLoading && (
                          <div className="col col-auto">
                            <span className="white--text">{t("Loading")}</span>
                          </div>
                        )}
                        {!methodsLoading && paymentMethods.length === 0 && (
                          <div className="col col-auto">
                            <span style={{ color: "black" }}>{t("NoPaymentMethods")}</span>
                          </div>
                        )}
                        {paymentMethods.map((m) => (
                          <div className="col col-auto" key={m._id}>
                            <button
                              type="button"
                              value={m.bank_name}

                              onClick={() => {

                                getBankDetail(m._id);
                                setSelectedMethod(m);

                              }}
                              className={
                                "v-btn v-btn--outlined theme--light v-size--default gateway-button" +
                                (selectedMethod?._id === m._id
                                  ? " active-gateway-button v-btn--active"
                                  : "")
                              }
                              style={{ height: "auto" }}
                            >
                              <span className="v-btn__content">
                                <img src={getIcon(m)} className="paymentChannelIcon" />
                                <label className="method_payment">
                                  {startCase(m.bank_name)}
                                </label>
                                <div className="tag-recommond" />
                                {!!m.chargePercentage && (
                                  <span
                                    className="percent-label ma-0 pa-1 v-chip v-chip--label theme--light v-size--x-small"
                                    style={{
                                      backgroundColor: "rgb(245, 105, 8)",
                                      borderColor: "rgb(245, 105, 8)",
                                    }}
                                  >
                                    <span className="v-chip__content">
                                      <p className="ma-0 pa-0" style={{ color: "rgb(255, 255, 255)" }}>
                                        + {m.chargePercentage} %
                                      </p>
                                    </span>
                                  </span>
                                )}
                              </span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {!isEmpty(watch("bankAccount")) && (
                      <>
                        <div
                          _ngcontent-serverapp-c684031653
                          className="ng-tns-c684031653-19 ng-trigger ng-trigger-staggerFadeAnimation ng-star-inserted"
                        >
                          <div className="section-container">
                            <div className="funds-title-wrapper">
                              <label className="funds-title">{t("Account_Name")}</label>
                            </div>

                            <div
                              _ngcontent-serverapp-c684031653
                              className="input-group money ng-tns-c684031653-19"
                            >
                              <input
                                _ngcontent-serverapp-c684031653
                                className="ng-tns-c684031653-19 ng-untouched ng-pristine ng-valid"
                                type="text"
                                readOnly
                                {...register("accountName")}
                              />
                            </div>
                          </div>
                        </div>{" "}
                        <div
                          _ngcontent-serverapp-c684031653
                          className="ng-tns-c684031653-19 ng-trigger ng-trigger-staggerFadeAnimation ng-star-inserted"
                        >
                          <div className="section-container">
                            <div className="funds-title-wrapper">
                              <label className="funds-title">{t("Bank_Account")}</label>
                            </div>

                            <div className="input-group money relative flex items-center"> {/* Left Icon */} 

                                 {/* Account Number */} 
                                 <input className="w-full pr-[80px] pl-[40px]" readOnly type="text" value="0161*******" name="bankAccount" /> 
                                 {/* Copy Button */} 
                                

                                 <button type="button" onClick={() => handleCopy() } className=" absolute right-2 top-1/2 -translate-y-1/2 rounded bg-[#ffffff1a] border border-[#ffffff26] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#ffffff2b] active:scale-95 transition-all " > Copy </button>
                                 </div>
                          </div>
                        </div>{" "}
                      </>
                    )}
                    {/* ---- Deposit Channel (dynamic, from apiPath.getBank) ---- */}
                    {/* <div className="section-container">
                      <div className="funds-title-wrapper">
                        <label className="funds-title">{t("DepositChannel")}</label>
                      </div>
                      <div className="funds-divider" />
                      <div className="row button-container no-gutters">
                        {channelsLoading && (
                          <div className="col col-auto">
                            <span className="white--text">{t("Loading")}</span>
                          </div>
                        )}
                        {!channelsLoading && channels.length === 0 && (
                          <div className="col col-auto">
                            <span style={{ color: "black" }} className="">{t("NoDepositChannels")}</span>
                          </div>
                        )}
                        {channels.map((c) => (
                          <button
                            key={c._id}
                            type="button"
                            value={c.bank_name}
                            onClick={() => setSelectedChannel(c)}
                            className={
                              "v-btn v-btn--outlined theme--light v-size--default gateway-button" +
                              (selectedChannel?._id === c._id
                                ? " active-gateway-button v-btn--active"
                                : "")
                            }
                            style={{ height: "auto" }}
                          >
                            <span className="v-btn__content">
                              <div className="tag-recommond" />
                              <label className="gateway_name d-flex align-center">
                                <img src={getIcon(c)} />
                                {startCase(c.bank_name)}
                              </label>
                              <span className="channelTag white--text">(CASHOUT)</span>
                              {!!c.chargePercentage && (
                                <span
                                  className="percent-label gateway-label ma-0 pa-1 v-chip v-chip--label theme--light v-size--x-small"
                                  style={{
                                    backgroundColor: "rgb(245, 105, 8)",
                                    borderColor: "rgb(245, 105, 8)",
                                  }}
                                >
                                  <span className="v-chip__content">
                                    <p className="ma-0 pa-0" style={{ color: "rgb(255, 255, 255)" }}>
                                      + {c.chargePercentage} %
                                    </p>
                                  </span>
                                </span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div> */}

                    {/* ---- Amount ---- */}
                    <div className="section-container">
                      <div className="funds-title-wrapper">
                        <label className="funds-title">{t("Amount")}</label>
                      </div>
                      <div className="funds-divider" />
                      <div className="row button-container no-gutters">
                        {AMOUNT_PRESETS.map((val) => (
                          <div key={val}>
                            <button
                              type="button"
                              value="amount"
                              onClick={() =>
                                setValue("amount", val, { shouldValidate: true, shouldDirty: true })
                              }
                              className={
                                "gateway-button v-btn v-btn--outlined theme--light v-size--default" +
                                (Number(watchedAmount) === val
                                  ? " active-gateway-button v-btn--active"
                                  : "")
                              }
                              style={{ height: "auto", width: "76px" }}
                            >
                              <span className="v-btn__content">
                                <label className="amount_cls">{val}</label>
                              </span>
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="v-input funds-input v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--prefix v-text-field--is-booted v-text-field--placeholder">
                        <div className="v-input__control">
                          <div className="v-input__slot">
                            <div className="v-text-field__slot">
                              <div className="v-text-field__prefix">৳</div>
                              <input
                                id="input-332"
                                placeholder={t("MinMaxAmountPlaceholder", { min: formatWithCommas(limit?.minDeposit), max: formatWithCommas(limit?.maxDeposit) })}
                                type="number"
                                {...register("amount", {
                                  required: { value: true, message: t("PleaseEnterAmount") },
                                  pattern: {
                                    value: /^[0-9]*$/,
                                    message: t("PleaseEnterValidNumber"),
                                  },
                                  validate: (value) => {
                                    if (!inRange(Number(value), Number(limit?.minDeposit), Number(limit?.maxDeposit) + 1)) {
                                      return t("DepositAmountRange", { min: limit?.minDeposit, max: limit?.maxDeposit });
                                    }
                                    return true;
                                  },
                                })}
                              />
                            </div>
                            <div className="v-input__append-inner">
                              <div className="v-input__icon v-input__icon--clear">
                                <button
                                  type="button"
                                  aria-label={t("ClearIcon")}
                                  onClick={() => setValue("amount", "", { shouldValidate: true })}
                                  className="v-icon notranslate v-icon--link mdi mdi-close theme--light"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="v-text-field__details">
                            <div className="v-messages theme--light">
                              <div className="v-messages__wrapper">
                                {errors?.amount?.message && (
                                  <span style={{ color: "#ff5252" }}>{errors.amount.message}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Transaction id ---- */}
                    <div className="section-container">
                      <div className="funds-title-wrapper">
                        <label className="funds-title">{t("Transaction_ID")}</label>
                      </div>
                      <div className="funds-divider" />


                      <div className="v-input funds-input v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--prefix v-text-field--is-booted v-text-field--placeholder">
                        <div className="v-input__control">
                          <div className="v-input__slot">
                            <div className="v-text-field__slot">
                              <div className="v-text-field__prefix">৳</div>
                              <input
                                _ngcontent-serverapp-c684031653
                                className="ng-tns-c684031653-19 ng-untouched ng-pristine ng-valid"
                                placeholder={"Transaction Id"}
                                type="text"
                                // inputMode="decimal"
                                {...register("transactionId", {
                                  required: {
                                    value: true,
                                    message: t(
                                      "enter_Transaction_ID",
                                    ),
                                  },
                                  pattern: {
                                    value:
                                      validationRules.removeWhitespace,
                                    message:
                                      "Please enter only number or character",
                                  },
                                })}
                              />

                            </div>

                          </div>
                          <div className="v-text-field__details">
                            <div className="v-messages theme--light">
                              <div className="v-messages__wrapper">
                                {errors?.transactionId?.message && (
                                  <span style={{ color: "#ff5252" }}>{errors.transactionId.message}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>


                    <div
                      _ngcontent-serverapp-c684031653
                      className="ng-tns-c684031653-19 ng-trigger ng-trigger-staggerFadeAnimation ng-star-inserted"
                    >



                      <div className="section-container">
                        <div className="funds-title-wrapper">
                          <label className="funds-title"> {t("Upload_Receipt")}</label>
                        </div>
                        <div
                          _ngcontent-serverapp-c684031653
                          className="input-group money ng-tns-c684031653-19"
                        >
                          <input
                            style={{ paddingTop: "7px" }}
                            _ngcontent-serverapp-c684031653
                            className="ng-tns-c684031653-19 ng-untouched ng-pristine ng-valid"
                            placeholder={"Transaction Id"}
                            type="file"
                            accept="image/png, image/jiffy, image/jpeg, image/jpg"
                            onChange={(e) => {
                              setImage(e.target.files[0]);
                            }}
                          />
                        </div>
                        {image !== "" && (
                          <div
                            _ngcontent-serverapp-c684031653
                            className="member-content ng-tns-c684031653-19 ng-star-inserted"
                          >
                            <div
                              _ngcontent-serverapp-c684031653
                              className="button btn-primary ng-tns-c684031653-19"
                            >
                              <button
                                type="submit"
                                className="ng-tns-c684031653-19"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  width: "100%",
                                  height: "100%",
                                  color: "white",
                                  fontWeight: "600",
                                  fontSize: "20px",
                                }}
                              >
                                {t("RemoveImage")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* ---- Deposit Bonus ---- */}
                    <div className="row section-container no-gutters">
                      <div className="funds-title-wrapper">
                        <label className="funds-title">{t("DepositBonus")}</label>
                      </div>
                      <div className="funds-divider" />
                      <div className="mobile">
                        <div className="row no-gutters justify-space-between">
                          <div className="col col-10" />
                          <div className="col col-2" />
                          <div className="col col-12">
                            <div className="v-input input-field v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined v-select">
                              <div className="v-input__control">
                                <div role="button" aria-haspopup="listbox" aria-expanded="false" className="v-input__slot">
                                  <fieldset aria-hidden="true">
                                    <legend style={{ width: "0px" }}>
                                      <span className="notranslate">​</span>
                                    </legend>
                                  </fieldset>
                                  <div className="v-select__slot">
                                    <select
                                      value={offer}
                                      onChange={onBonusChange}
                                      style={{ background: "transparent", border: "none", color: "inherit", width: "100%" }}
                                    >
                                      <option value="">{t("NoBonus")}</option>
                                      {dataOffer?.map((res) => (
                                        <option key={res?._id} value={res?._id}>
                                          {res?.title}
                                        </option>
                                      ))}
                                    </select>
                                    <div className="v-input__append-inner">
                                      <div className="v-input__icon v-input__icon--append">
                                        <i
                                          aria-hidden="true"
                                          className="v-icon notranslate mdi mdi-menu-down theme--light"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row pa-3 no-gutters">
                      <button
                        type="submit"
                        disabled={loading}
                        className="pa-2 v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default deposit-button buttonPrimary"
                      >
                        <span className="v-btn__content">
                          {loading ? t("Processing", { countdown }) : t("Deposit")}
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div role="dialog" className="v-dialog__container" style={{ position: "relative" }} />
      </div>
    </Layout>
  );
};

export default Deposit;
